import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { apiGet, operationsClient } from '@/lib/api';
import type { Estimate } from '@/types/estimate';
import {
  deriveEstimateFormValuesFromDuplicate,
  deriveExistingEstimateAddressState,
  formatEstimateAddress,
  type EstimateContactAddress,
} from '@/routes/_dashboard/dashboard/estimates/_shared';

interface UseEstimateDuplicateSeedArgs {
  duplicateId?: string;
  duplicateEstimate?: Estimate;
  applyDraftState: (next: ReturnType<typeof deriveEstimateFormValuesFromDuplicate>) => void;
  applyAddress: (addr: {
    street?: string;
    street2?: string | null;
    city?: string;
    state?: string;
    zip?: string;
  }) => void;
  setBillToId: Dispatch<SetStateAction<string | null | undefined>>;
  setContactAddresses: Dispatch<SetStateAction<EstimateContactAddress[]>>;
  setAddressMode: Dispatch<SetStateAction<'select' | 'manual'>>;
  setSelectedAddressIndex: Dispatch<SetStateAction<number | null>>;
  setBillingMode: Dispatch<SetStateAction<'select' | 'manual'>>;
  setSelectedBillingIndex: Dispatch<SetStateAction<number | null>>;
  setCustomerAddress: Dispatch<SetStateAction<string>>;
  setBaStreet: Dispatch<SetStateAction<string>>;
  setBaStreet2: Dispatch<SetStateAction<string>>;
  setBaCity: Dispatch<SetStateAction<string>>;
  setBaState: Dispatch<SetStateAction<string>>;
  setBaZip: Dispatch<SetStateAction<string>>;
}

export function useEstimateDuplicateSeed({
  duplicateId,
  duplicateEstimate,
  applyDraftState,
  applyAddress,
  setBillToId,
  setContactAddresses,
  setAddressMode,
  setSelectedAddressIndex,
  setBillingMode,
  setSelectedBillingIndex,
  setCustomerAddress,
  setBaStreet,
  setBaStreet2,
  setBaCity,
  setBaState,
  setBaZip,
}: UseEstimateDuplicateSeedArgs) {
  const didDuplicateRef = useRef(false);

  useEffect(() => {
    if (!duplicateId || !duplicateEstimate || didDuplicateRef.current) return;

    didDuplicateRef.current = true;
    const estimate = duplicateEstimate;
    applyDraftState(deriveEstimateFormValuesFromDuplicate(estimate));

    const parseLegacyBillingAddress = (address?: string | null): Estimate['billingAddress'] => {
      if (!address) return null;
      const parts = address
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
      if (parts.length < 4) return null;

      const zip = parts[parts.length - 1] ?? '';
      const state = parts[parts.length - 2] ?? '';
      const city = parts[parts.length - 3] ?? '';
      const streetParts = parts.slice(0, -3);

      return {
        street: streetParts[0] ?? '',
        ...(streetParts.length > 1 && { street2: streetParts.slice(1).join(', ') }),
        city,
        state,
        zip,
      };
    };

    const applyManualBillingAddress = () => {
      const billingAddress =
        estimate.billingAddress ?? parseLegacyBillingAddress(estimate.customer?.address);

      if (!billingAddress) {
        setBillingMode('select');
        setSelectedBillingIndex(null);
        setCustomerAddress(estimate.customer?.address ?? '');
        setBaStreet('');
        setBaStreet2('');
        setBaCity('');
        setBaState('');
        setBaZip('');
        return;
      }

      setBillingMode('manual');
      setSelectedBillingIndex(null);
      setCustomerAddress(formatEstimateAddress(billingAddress));
      setBaStreet(billingAddress.street ?? '');
      setBaStreet2(billingAddress.street2 ?? '');
      setBaCity(billingAddress.city ?? '');
      setBaState(billingAddress.state ?? '');
      setBaZip(billingAddress.zip ?? '');
    };

    const contactId = estimate.contactId;
    if (!contactId) {
      setBillToId(undefined);
      setContactAddresses([]);
      setSelectedAddressIndex(null);
      if (estimate.serviceAddress) {
        setAddressMode('manual');
        applyAddress(estimate.serviceAddress);
      }
      applyManualBillingAddress();
      return;
    }

    setBillToId((estimate as { billToId?: string | null }).billToId ?? null);
    apiGet<Record<string, unknown>>(operationsClient, `/contacts/${contactId}`)
      .then((contact) => {
        const addresses = (contact.addresses ?? []) as EstimateContactAddress[];
        const next = deriveExistingEstimateAddressState({
          addresses,
          serviceAddress: estimate.serviceAddress ?? undefined,
          billingAddress: estimate.customer?.address ?? '',
          billingAddressStructured: estimate.billingAddress ?? undefined,
        });

        setContactAddresses(addresses);
        setAddressMode(next.address.addressMode);
        setSelectedAddressIndex(next.address.selectedAddressIndex);

        if (next.address.serviceAddress) {
          applyAddress(next.address.serviceAddress);
        }

        setBillingMode(next.billing.billingMode);
        setSelectedBillingIndex(next.billing.selectedBillingIndex);
        setCustomerAddress(next.billing.customerAddress);

        if (next.billing.billingMode === 'manual' && next.billing.manualBillingAddress) {
          const ba = next.billing.manualBillingAddress;
          setBaStreet(ba.street ?? '');
          setBaStreet2(ba.street2 ?? '');
          setBaCity(ba.city ?? '');
          setBaState(ba.state ?? '');
          setBaZip(ba.zip ?? '');
        } else {
          setBaStreet('');
          setBaStreet2('');
          setBaCity('');
          setBaState('');
          setBaZip('');
        }
      })
      .catch(() => {
        setContactAddresses([]);
        setSelectedAddressIndex(null);
        setSelectedBillingIndex(null);
        if (estimate.serviceAddress) {
          setAddressMode('manual');
          applyAddress(estimate.serviceAddress);
        }
        applyManualBillingAddress();
      });
  }, [
    duplicateId,
    duplicateEstimate,
    applyDraftState,
    applyAddress,
    setBillToId,
    setContactAddresses,
    setAddressMode,
    setSelectedAddressIndex,
    setBillingMode,
    setSelectedBillingIndex,
    setCustomerAddress,
    setBaStreet,
    setBaStreet2,
    setBaCity,
    setBaState,
    setBaZip,
  ]);
}
